import { Link } from "react-router-dom";
import { Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { company } from "../../data/site";

const servicesLinks = [
  { label: "AI & Machine Learning", to: "/services/ai-machine-learning" },
  { label: "Data Engineering", to: "/services/data-engineering" },
  { label: "Cloud & DevOps", to: "/services/cloud-devops" },
  { label: "Enterprise Applications", to: "/services/enterprise-applications" },
  { label: "Engineering Solutions", to: "/services/engineering-solutions" },
  { label: "IT Staffing", to: "/services/it-staffing" },
];

const industriesLinks = [
  { label: "Healthcare", to: "/industries/healthcare" },
  { label: "Financial Services", to: "/industries/financial-services" },
  { label: "Manufacturing", to: "/industries/manufacturing" },
  { label: "Semiconductor", to: "/industries/semiconductor-electronics" },
  { label: "Engineering", to: "/industries/engineering-technology" },
  { label: "Automotive", to: "/industries/automotive" },
  { label: "Logistics", to: "/industries/logistics-supply-chain" },
  { label: "Energy", to: "/industries/energy" },
];

const companyLinks = [
  { label: "About", to: "/about" },
  { label: "Industries", to: "/industries" },
  { label: "Insights", to: "/insights" },
  { label: "Careers", to: "/careers" },
  { label: "Candidate Login", to: "/portal/login" },
  { label: "Contact", to: "/contact" },
];

const Column = ({ title, links }) => (
  <div>
    <h4 className="mb-5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
      {title}
    </h4>
    <ul className="space-y-3">
      {links.map((l) => (
        <li key={l.to}>
          <Link
            to={l.to}
            className="text-sm text-slate-400 transition-colors duration-200 hover:text-sky-400"
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default function Footer() {
  return (
    <footer className="relative border-t border-white/5 bg-ink-950 px-6 pb-10 pt-24" data-testid="footer">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <img src="/amvi-wordmark-v2.png" alt="AMVI Technologies logo" className="h-14 w-auto object-contain" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">
              Technology + Talent Solutions. Engineering the future with AI, data, cloud and specialized talent.
            </p>
            <div className="mt-6 space-y-3 text-sm text-slate-400">
              <a href={company.phoneHref} className="flex items-center gap-3 hover:text-sky-400" data-testid="footer-phone">
                <Phone className="h-4 w-4 text-sky-400" /> {company.phone}
              </a>
              <a href={`mailto:${company.email}`} className="flex items-center gap-3 hover:text-sky-400" data-testid="footer-email">
                <Mail className="h-4 w-4 text-sky-400" /> {company.email}
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
                <span>
                  {company.address.line1}<br />
                  {company.address.city}, {company.address.state} {company.address.zip}
                </span>
              </div>
            </div>
            <a
              href={company.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-300 transition-colors hover:border-sky-400/60 hover:text-sky-400"
              aria-label="LinkedIn"
              data-testid="footer-linkedin"
            >
              <Linkedin className="h-4 w-4" />
            </a>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-8">
            <Column title="Services" links={servicesLinks} />
            <Column title="Industries" links={industriesLinks} />
            <Column title="Company" links={companyLinks} />
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/5 pt-8 text-xs text-slate-500 sm:flex-row">
          <p>© {new Date().getFullYear()} {company.legalName}. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-6">
            <Link to="/privacy" className="hover:text-slate-300">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-300">Terms of Use</Link>
            <Link to="/cookies" className="hover:text-slate-300">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
